import { OverflowList } from "@theme/critical";
import VariantPicker from "@theme/variant-picker";
import { Component } from "@theme/component";
import {
  debounce,
  isDesktopBreakpoint,
  mediaQueryLarge,
  requestYieldCallback,
} from "@theme/utilities";
import {
  ThemeEvents,
  VariantSelectedEvent,
  VariantUpdateEvent,
} from "@theme/events";
import { morph } from "@theme/morph";

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ["productCardLink"];
  /** @type {string | null} */
  #hoverBaseMediaId = null;
  /** @type {string | null} */
  #hoverVideoMediaId = null;

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, "_blank");
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement))
      throw new Error("Product card link not found");
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(
      ThemeEvents.variantSelected,
      this.#handleVariantSelected
    );
    mediaQueryLarge.addEventListener("change", this.#handleQuickAdd);

    this.addEventListener("click", this.navigateToProduct);

    // Preload the next image to avoid white flashes on previewImage
    setTimeout(() => {
      const cardGallery = this.querySelector('.card-gallery');
      if (cardGallery) {
        const imagesContainer = cardGallery.querySelector('.card-gallery__images');
        if (imagesContainer) {
          const imageWrappers = Array.from(imagesContainer.querySelectorAll('.card-gallery__image-wrapper:not([hidden])'));
          if (imageWrappers.length > 1) {
            const nextImage = imageWrappers[1]?.querySelector('img[loading="lazy"]');
            nextImage?.removeAttribute("loading");
          }
        }
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.navigateToProduct);
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener("pointerenter", this.#fetchProductPageHandler);
    this.removeEventListener("focusin", this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener("pointerenter", this.#fetchProductPageHandler);
      this.addEventListener("focusin", this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#hoverBaseMediaId = null;
    this.#hoverVideoMediaId = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute("data-no-swatch-selected");

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector(
      "swatches-variant-picker-component overflow-list"
    );
    const isActiveOverflowList = overflowList?.querySelector(
      '[slot="overflow"]'
    )
      ? true
      : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent("reflow", {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(
      `product-price [ref='priceContainer']`
    )[1];
    const newPriceElement = event.detail.data.html.querySelector(
      `product-price [ref='priceContainer']`
    );

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement =
      event.detail.data.html?.querySelector("product-card a");
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector("product-card-link")
      ?.getAttribute("data-featured-media-url");

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest("product-card-link"))
      this.closest("product-card-link")?.setAttribute(
        "data-featured-media-url",
        featuredMediaUrl
      );

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute("href")?.trim() === "") return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll("input:checked")
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === "true");
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector(".add-to-cart__button button");

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const cardGallery = this.querySelector('.card-gallery');
    if (!cardGallery) return;

    const imagesContainer = cardGallery.querySelector('.card-gallery__images');
    if (!imagesContainer) return;

    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId =
      this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (selectedImageId) {
      const imageWrappers = Array.from(imagesContainer.querySelectorAll('.card-gallery__image-wrapper[data-variant-image="true"]'));

      for (const wrapper of imageWrappers) {
        const mediaId = wrapper.getAttribute("data-media-id");
        if (mediaId === selectedImageId) {
          wrapper.removeAttribute("hidden");
          wrapper.classList.add('card-gallery__image-wrapper--active');
        } else {
          wrapper.setAttribute("hidden", "");
          wrapper.classList.remove('card-gallery__image-wrapper--active');
        }
      }

      // Ensure only one active image
      const allWrappers = Array.from(imagesContainer.querySelectorAll('.card-gallery__image-wrapper:not([hidden])'));
      const selectedWrapper = allWrappers.find(w => w.getAttribute("data-media-id") === selectedImageId);
      if (selectedWrapper) {
        allWrappers.forEach((wrapper) => {
          wrapper.classList.remove('card-gallery__image-wrapper--active');
        });
        selectedWrapper.classList.add('card-gallery__image-wrapper--active');
      }
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll("input[data-variant-id]");
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector("swatches-variant-picker-component");
  }
  #getVisibleImageWrappers(imagesContainer) {
    return Array.from(
      imagesContainer.querySelectorAll(".card-gallery__image-wrapper:not([hidden])")
    );
  }

  #playWrapperMedia(wrapper) {
    const deferredMedia = wrapper.querySelector("deferred-media");
    if (deferredMedia) {
      if (typeof deferredMedia.showDeferredMedia === "function") {
        deferredMedia.showDeferredMedia();
      }
      if (typeof deferredMedia.playMedia === "function") {
        deferredMedia.playMedia();
      }
    }

    const video = wrapper.querySelector("video");
    const playPromise = video?.play();
    playPromise?.catch(() => {});
  }

  #pauseWrapperMedia(wrapper) {
    const deferredMedia = wrapper.querySelector("deferred-media");
    if (deferredMedia && typeof deferredMedia.pauseMedia === "function") {
      deferredMedia.pauseMedia();
    }

    wrapper.querySelector("video")?.pause();
  }

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const cardGallery = this.querySelector('.card-gallery');
    if (!cardGallery) return;

    const imagesContainer = cardGallery.querySelector('.card-gallery__images');
    if (!imagesContainer) return;

    this.resetVariant.cancel();

    const imageWrappers = this.#getVisibleImageWrappers(imagesContainer);
    const variantWrapper = imageWrappers.find(
      (w) => w.getAttribute("data-media-id") === id
    );

    // Keep the selected variant image stable on hover. We only preload.
    variantWrapper?.querySelector('img[loading="lazy"]')?.removeAttribute("loading");
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== "mouse") return;

    const cardGallery = this.querySelector('.card-gallery');
    if (!cardGallery) return;

    const imagesContainer = cardGallery.querySelector('.card-gallery__images');
    if (!imagesContainer) return;

    const imageWrappers = this.#getVisibleImageWrappers(imagesContainer);
    if (imageWrappers.length <= 1) return;

    const activeWrapper = imageWrappers.find((wrapper) =>
      wrapper.classList.contains("card-gallery__image-wrapper--active")
    );
    if (!activeWrapper) return;

    // Always preview the second visible gallery item on hover.
    const hoverWrapper = imageWrappers[1];
    if (!hoverWrapper || hoverWrapper === activeWrapper) return;

    this.#hoverBaseMediaId = activeWrapper.getAttribute("data-media-id");
    this.#hoverVideoMediaId = hoverWrapper.getAttribute("data-media-id");

    activeWrapper.classList.remove("card-gallery__image-wrapper--active");
    hoverWrapper.classList.add("card-gallery__image-wrapper--active");
    this.#playWrapperMedia(hoverWrapper);
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== "mouse") return;

    const cardGallery = this.querySelector('.card-gallery');
    if (!cardGallery) return;

    const imagesContainer = cardGallery.querySelector('.card-gallery__images');
    if (!imagesContainer) return;

    const imageWrappers = this.#getVisibleImageWrappers(imagesContainer);
    if (imageWrappers.length <= 1) return;

    const hoveredVideoWrapper = this.#hoverVideoMediaId
      ? imageWrappers.find(
          (wrapper) =>
            wrapper.getAttribute("data-media-id") === this.#hoverVideoMediaId
        )
      : imageWrappers.find(
          (wrapper) =>
            wrapper.classList.contains("card-gallery__image-wrapper--active") &&
            (wrapper.querySelector("deferred-media") ||
              wrapper.querySelector("video") ||
              wrapper.querySelector("iframe"))
        );

    hoveredVideoWrapper && this.#pauseWrapperMedia(hoveredVideoWrapper);

    const baseWrapper = this.#hoverBaseMediaId
      ? imageWrappers.find(
          (wrapper) =>
            wrapper.getAttribute("data-media-id") === this.#hoverBaseMediaId
        )
      : null;

    if (baseWrapper) {
      imageWrappers.forEach((wrapper) => {
        wrapper.classList.remove("card-gallery__image-wrapper--active");
      });
      baseWrapper.classList.add("card-gallery__image-wrapper--active");
    } else if (this.variantPicker) {
      this.#resetVariant();
    } else {
      imageWrappers.forEach((wrapper) => {
        wrapper.classList.remove("card-gallery__image-wrapper--active");
      });
      imageWrappers[0].classList.add("card-gallery__image-wrapper--active");
    }

    this.#hoverBaseMediaId = null;
    this.#hoverVideoMediaId = null;
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const cardGallery = this.querySelector('.card-gallery');
    if (!cardGallery) return;

    const imagesContainer = cardGallery.querySelector('.card-gallery__images');
    if (!imagesContainer) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const mediaId = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (mediaId) {
        const variantWrapper = imagesContainer.querySelector(`[data-media-id="${mediaId}"]`);
        if (variantWrapper && !variantWrapper.hasAttribute('hidden')) {
          const imageWrappers = Array.from(imagesContainer.querySelectorAll('.card-gallery__image-wrapper:not([hidden])'));
          imageWrappers.forEach((wrapper) => {
            wrapper.classList.remove('card-gallery__image-wrapper--active');
          });
          variantWrapper.classList.add('card-gallery__image-wrapper--active');
          return;
        }
      }
    }

    // No variant selected - use first image
    const imageWrappers = Array.from(imagesContainer.querySelectorAll('.card-gallery__image-wrapper:not([hidden])'));
    if (imageWrappers.length > 0) {
      imageWrappers.forEach((wrapper) => {
        wrapper.classList.remove('card-gallery__image-wrapper--active');
      });
      imageWrappers[0].classList.add('card-gallery__image-wrapper--active');
    }
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute("data-no-navigation")) return;

    const interactiveElement = event.target.closest(
      'button, input, label, select, [tabindex="1"]'
    );

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute("id");
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest("li");
    url.hash = productCardAnchor;
    if (parent?.dataset.page) {
      url.searchParams.set("page", parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, "", url.toString());
      });
    }

    const targetLink = event.target.closest("a");
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest("product-card");

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleCardVariantUrlUpdate.bind(this)
    );
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (
      this.pendingVariantId &&
      this.parentProductCard instanceof ProductCard
    ) {
      const currentUrl = new URL(
        this.parentProductCard.refs.productCardLink.href
      );
      currentUrl.searchParams.set("variant", this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput =
      event.target instanceof HTMLInputElement &&
      event.target.name?.includes("-swatch");
    const clickedSwatch = event.target;
    const availableCount = parseInt(
      clickedSwatch.dataset.availableCount || "0"
    );
    const firstAvailableVariantId =
      clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split("?")[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set("variant", firstAvailableVariantId);
      url.searchParams.set("section_id", "section-rendering-product-card");

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get("swatches-variant-picker-component")) {
  customElements.define(
    "swatches-variant-picker-component",
    SwatchesVariantPickerComponent
  );
}
